import { geoAlbersUsa, geoPath } from 'd3-geo';
import { feature } from 'topojson';
import usMap from '../design-handoff/design_handoff_redistricting_results/design/data/us-map.json';
import stateMaps from '../design-handoff/design_handoff_redistricting_results/design/data/state-maps.json';
import stateLines from '../data/ne_110m_admin_1_states_provinces_lines.json';
import {
  PARTY, GRAY, TIE_FILL, DESKTOP_COLS, DESKTOP_ROWS, RUNOFF_FILL, districtName, districtCode, isTie,
  partyStyle, runoffWords, stateSquares, marginText,
} from './model';

const ADJ = {
  FL: [0.78, 0.45],
  LA: [0.38, 0.45],
  CA: [0.4, 0.55],
  TX: [0.58, 0.52],
};

function el(tag, className) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

function svgEl(tag) {
  return document.createElementNS('http://www.w3.org/2000/svg', tag);
}

function chevron(open) {
  const node = el('span', `chev${open ? ' is-open' : ''}`);
  node.innerHTML = '<svg width="10" height="10" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
  return node;
}

// Solid for a called winner, the light tint for a leader, gray otherwise.
// Covers independents and third parties too ('O'), via their own party colors.
function seatColor(seat) {
  if (seat.runoff && !seat.called) return RUNOFF_FILL;
  const party = partyStyle(seat, seat.shown);
  if (!party) return isTie(seat) ? TIE_FILL : GRAY;
  return seat.called ? party.fill : party.tint;
}

function check(fill) {
  const icon = svgEl('svg');
  icon.setAttribute('width', '20');
  icon.setAttribute('height', '20');
  icon.setAttribute('viewBox', '0 0 20 20');
  icon.setAttribute('aria-label', 'Won the House');
  icon.innerHTML = `<circle cx="10" cy="10" r="10" fill="${fill}"></circle><path d="M5.8 10.3 8.7 13.1 14.3 7.2" fill="none" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>`;
  return icon;
}

function bounds(path) {
  const nums = path.match(/-?\d+\.?\d*/g) || [];
  const box = [1e9, 1e9, -1e9, -1e9];
  for (let i = 0; i + 1 < nums.length; i += 2) {
    const x = +nums[i];
    const y = +nums[i + 1];
    if (x < box[0]) box[0] = x;
    if (y < box[1]) box[1] = y;
    if (x > box[2]) box[2] = x;
    if (y > box[3]) box[3] = y;
  }
  return box;
}

// Albers USA fitted to us-map.json's 960×600 district paths (from shared tri-state corners).
const NATIONAL_PROJECTION = geoAlbersUsa().scale(1275.24).translate([479.92, 296.17]);
let borderPath = null;

function stateBorders() {
  if (!borderPath) {
    const lines = feature(stateLines, stateLines.objects.ne_110m_admin_1_states_provinces_lines);
    borderPath = geoPath(NATIONAL_PROJECTION)(lines);
  }
  return borderPath;
}

// `starred` states get an asterisk on their label, keyed to the footnote under the overview.
function nationalMap(party, codes, starred = []) {
  const selected = new Set(codes);
  let on = '';
  let off = '';
  const boxes = {};
  Object.keys(usMap.d).forEach((key) => {
    const state = key.split('-')[0];
    const path = usMap.d[key];
    if (!selected.has(state)) {
      off += path;
      return;
    }
    on += path;
    const box = boxes[state] || [1e9, 1e9, -1e9, -1e9];
    const next = bounds(path);
    box[0] = Math.min(box[0], next[0]);
    box[1] = Math.min(box[1], next[1]);
    box[2] = Math.max(box[2], next[2]);
    box[3] = Math.max(box[3], next[3]);
    boxes[state] = box;
  });

  const svg = svgEl('svg');
  svg.setAttribute('viewBox', '0 0 960 600');
  svg.setAttribute('width', '216');
  svg.setAttribute('height', '135');
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', `${PARTY[party].plural} redistricting states`);

  const offPath = svgEl('path');
  offPath.setAttribute('d', off);
  offPath.setAttribute('fill', '#DCDCDC');
  offPath.setAttribute('stroke', '#DCDCDC');
  offPath.setAttribute('stroke-width', '1.5');
  offPath.setAttribute('stroke-linejoin', 'round');
  const onPath = svgEl('path');
  onPath.setAttribute('d', on);
  onPath.setAttribute('fill', PARTY[party].fill);
  onPath.setAttribute('stroke', PARTY[party].fill);
  onPath.setAttribute('stroke-width', '1.5');
  onPath.setAttribute('stroke-linejoin', 'round');
  svg.appendChild(offPath);
  svg.appendChild(onPath);

  // State borders from Natural Earth's 1:110m state lines, projected onto the district map.
  const borders = svgEl('path');
  borders.setAttribute('d', stateBorders());
  borders.setAttribute('fill', 'none');
  borders.setAttribute('stroke', '#FFFFFF');
  borders.setAttribute('stroke-width', '2.5');
  borders.setAttribute('stroke-linejoin', 'round');
  svg.appendChild(borders);

  const labels = svgEl('g');
  labels.setAttribute('fill', '#FFFFFF');
  labels.setAttribute('stroke', PARTY[party].fill);
  labels.setAttribute('stroke-width', '12');
  labels.setAttribute('stroke-linejoin', 'round');
  labels.setAttribute('paint-order', 'stroke');
  labels.style.fontFamily = '"Public Sans", "CNN Sans", sans-serif';
  labels.style.fontSize = '40px';
  labels.style.fontWeight = '700';
  Object.keys(boxes).forEach((state) => {
    const box = boxes[state];
    const adjust = ADJ[state] || [0.5, 0.5];
    const text = svgEl('text');
    text.setAttribute('x', (box[0] + ((box[2] - box[0]) * adjust[0])).toFixed(0));
    text.setAttribute('y', (box[1] + ((box[3] - box[1]) * adjust[1])).toFixed(0));
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('dominant-baseline', 'central');
    text.textContent = starred.includes(state) ? `${state}*` : state;
    labels.appendChild(text);
  });
  svg.appendChild(labels);
  return svg;
}

function miniSquares(seats, color) {
  const grid = el('div', 'mini-squares');
  seats.forEach((seat) => {
    const cell = el('div', 'mini-cell');
    cell.title = districtName(seat) + (seat.targeted ? ' · targeted' : '');
    const box = el('div', 'mini-box');
    box.style.background = color(seat);
    cell.appendChild(box);
    grid.appendChild(cell);
  });
  return grid;
}

// Desktop mirrors the parties ("107 Democrats" … "Republicans 125"); with `nameFirst`
// (mobile, where the totals stack) both read name first.
function partyTotal(party, model, side, nameFirst = false) {
  const node = el('div', `party-total ${side}`);
  const row = el('div', 'party-line');
  row.style.color = PARTY[party].ink;
  const number = el('span', 'party-num');
  number.textContent = String(model.won[party]);
  const name = el('span', 'party-name');
  name.textContent = PARTY[party].plural;
  if (party === 'D' && !nameFirst) {
    if (model.control.D) row.appendChild(check(PARTY.D.fill));
    row.appendChild(number);
    row.appendChild(name);
  } else {
    row.appendChild(name);
    row.appendChild(number);
    if (model.control[party]) row.appendChild(check(PARTY[party].fill));
  }
  node.appendChild(row);

  {
    const { got, aim } = model.aims[party];
    const held = model.held[party];
    const line = el('div', 'party-sub is-parts');
    const parts = el('span', 'party-sub-parts');
    line.appendChild(parts);
    const flipped = el('span', 'party-sub-part');
    const swatch = el('i', 'swatch is-target');
    swatch.style.background = PARTY[party].fill;
    flipped.appendChild(swatch);
    const gotStrong = el('strong');
    gotStrong.textContent = String(got);
    flipped.appendChild(gotStrong);
    flipped.appendChild(document.createTextNode(` of ${aim} targets flipped`));
    parts.appendChild(flipped);
    if (held) {
      const other = PARTY[party === 'D' ? 'R' : 'D'].short;
      const targetWord = held === 1 ? 'target' : 'targets';
      const heldPart = el('span', 'party-sub-part');
      const heldStrong = el('strong');
      heldStrong.textContent = String(held);
      heldPart.appendChild(heldStrong);
      heldPart.appendChild(document.createTextNode(` ${other} ${targetWord} held`));
      parts.appendChild(heldPart);
    }
    node.appendChild(line);
    if (model.mode === 'partial' && model.leading[party]) {
      const leading = el('div', 'party-sub');
      const tint = el('i', 'swatch');
      tint.style.background = PARTY[party].tint;
      const count = el('strong');
      count.textContent = String(model.leading[party]);
      leading.appendChild(tint);
      leading.appendChild(document.createTextNode('Leading in '));
      leading.appendChild(count);
      node.appendChild(leading);
    }
  }
  return node;
}

// Seats won or led by independents and third parties, which count toward neither
// party's 218. Named for the party when they all share one ("Ind. 3"), else "Other".
function otherTotal(model, className) {
  if (!model.others.length) return null;
  const styles = otherStyles(model, () => true);
  const name = styles.length === 1 ? styles[0].short : 'Other';
  const won = model.others.filter(seat => seat.called).length;
  const leads = model.others.length - won;
  const node = el('div', `party-sub is-parts other-total ${className}`);
  const parts = el('span', 'party-sub-parts');
  node.appendChild(parts);
  const first = el('span', 'party-sub-part');
  // On mobile the strip's own squares sit beside the count, so it needs no swatch.
  if (styles.length === 1 && className !== 'is-stack') {
    const swatch = el('i', 'swatch');
    swatch.style.background = won ? styles[0].fill : styles[0].tint;
    first.appendChild(swatch);
  }
  const strong = (value) => {
    const count = el('strong');
    count.textContent = String(value);
    return count;
  };
  if (won) {
    first.appendChild(document.createTextNode(`${name} `));
    first.appendChild(strong(won));
    parts.appendChild(first);
    if (leads) {
      const lead = el('span', 'party-sub-part');
      lead.appendChild(document.createTextNode('Leading in '));
      lead.appendChild(strong(leads));
      parts.appendChild(lead);
    }
  } else {
    first.appendChild(document.createTextNode(`${name} leading in `));
    first.appendChild(strong(leads));
    parts.appendChild(first);
  }
  return node;
}

function seatGrid(cells, rows, columns, cell) {
  const grid = el('div', 'seat-grid');
  grid.style.gridTemplateRows = `repeat(${rows}, ${cell}px)`;
  grid.style.gridTemplateColumns = `repeat(${columns}, ${cell}px)`;
  cells.forEach((item) => {
    const seat = el('div', 'seat');
    seat.style.gridColumn = String(item.col);
    seat.style.gridRow = String(item.row);
    seat.style.padding = item.pad;
    ['--pad-t', '--pad-r', '--pad-b', '--pad-l'].forEach((name, index) => {
      seat.style.setProperty(name, item.pad.split(' ')[index] || '0px');
    });
    seat.style.zIndex = String(item.z);
    if (item.title) {
      seat.setAttribute('data-seat', item.id);
      seat.setAttribute('aria-label', item.title);
    }
    const fill = el('div', 'seat-fill');
    fill.style.background = item.fill;
    fill.style.boxShadow = item.shadow;
    fill.style.borderRadius = item.radius;
    if (item.clip && item.clip !== 'none') fill.style.clipPath = item.clip;
    seat.appendChild(fill);
    grid.appendChild(seat);
  });
  return grid;
}

function bracketRow(items) {
  const row = el('div', 'brackets');
  items.forEach((item) => {
    const tick = el('div', 'bracket');
    tick.style.left = `${item.left}px`;
    tick.style.width = `${item.width}px`;
    if (item.showLabel) {
      const label = el('span', `bracket-label${item.align === 'start' ? ' is-start' : ''}`);
      label.textContent = item.label;
      tick.appendChild(label);
    }
    row.appendChild(tick);
  });
  return row;
}

const CLUSTER_MIN_SPACE = 12;
const CLUSTER_LABEL_GAP = 14;

function textWidth(node) {
  const range = document.createRange();
  range.selectNodeContents(node);
  return range.getBoundingClientRect().width;
}

export function spaceAimClusters(root) {
  root.querySelectorAll('.aim-clusters').forEach((row) => {
    const clusters = row.querySelectorAll('.aim-cluster');
    const arrow = row.querySelector('.aim-between');
    if (clusters.length !== 2 || !arrow) return;
    const blocks = Array.prototype.map.call(clusters, (cluster) => {
      const squares = cluster.querySelector('.mini-squares').getBoundingClientRect();
      const label = cluster.querySelector('.micro');
      const overhang = label ? Math.max(0, (textWidth(label) - squares.width) / 2) : 0;
      return { squares, overhang };
    });
    const arrowBox = arrow.getBoundingClientRect();
    const half = Math.max(
      (arrowBox.width / 2) + CLUSTER_MIN_SPACE,
      (blocks[0].overhang + blocks[1].overhang + CLUSTER_LABEL_GAP) / 2,
    );
    const side = Math.round(half - (arrowBox.width / 2));
    const top = Math.round((blocks[0].squares.height - arrowBox.height) / 2);
    arrow.style.margin = `${Math.max(0, top)}px ${side}px 0`;
  });
}

const NOTE_MAX_WIDTH = 150;
const NOTE_MIN_GUTTER = 120;
const NOTE_INSET = 12;

export function placeAimNote(root) {
  const cols = root.querySelector('.aim-cols');
  const column = cols && cols.querySelector('.aim-col-R');
  const note = cols && cols.parentNode.querySelector('.aim-note.is-floating');
  const arrow = column && column.querySelector('.aim-arrow');
  const map = column && column.querySelector('.nat-wrap');
  if (!note || !arrow || !map) return;

  // The note floats beside the Republican map; reset it there before measuring.
  const row = cols.parentNode.querySelector('.aim-note-row.is-above');
  if (note.parentNode !== column) column.insertBefore(note, arrow);
  if (row) row.remove();
  note.classList.remove('below');
  note.style.cssText = '';
  arrow.style.display = '';
  const box = cols.getBoundingClientRect();
  const mapBox = map.getBoundingClientRect();
  const outer = cols.closest('.overview') || cols;
  const gutterLeft = mapBox.right - box.left;
  const gutterRight = (outer.getBoundingClientRect().right - NOTE_INSET) - box.left;
  const gutter = gutterRight - gutterLeft;

  // Too tight to float: center it under the title, above both maps, instead.
  if (gutter < NOTE_MIN_GUTTER) {
    const above = document.createElement('div');
    above.className = 'aim-note-row is-above';
    cols.parentNode.insertBefore(above, cols);
    above.appendChild(note);
    note.classList.add('below');
    arrow.style.display = 'none';
    return;
  }

  const width = Math.min(NOTE_MAX_WIDTH, gutter - 16);
  const left = gutterLeft + ((gutter - width) / 2);
  const top = (mapBox.top - box.top) + 12;
  note.style.left = `${left}px`;
  note.style.top = `${top}px`;
  note.style.width = `${width}px`;
  note.style.visibility = 'visible';

  const noteBottom = top + note.offsetHeight;
  const tail = { x: left + Math.min(28, width * 0.25), y: noteBottom + 6 };
  // Point at the cluster nearest the note (the rightmost one).
  const clusterSquares = column.querySelectorAll('.aim-cluster .mini-squares');
  const winners = clusterSquares[clusterSquares.length - 1];
  let head;
  if (winners) {
    const squares = winners.getBoundingClientRect();
    head = { x: (squares.right - box.left) + 10, y: (squares.top - box.top) + (squares.height / 2) };
  } else {
    head = { x: (mapBox.left - box.left) + (mapBox.width * 0.9), y: (mapBox.top - box.top) + (mapBox.height * 0.53) };
  }
  const spanX = Math.max(1, tail.x - head.x);
  const spanY = Math.max(1, head.y - tail.y);
  const start = { x: tail.x, y: tail.y + (spanY * 0.55) };
  const bend = { x: head.x + (spanX * 0.55), y: head.y - (spanY * 0.12) };
  const dx = head.x - bend.x;
  const dy = head.y - bend.y;
  const length = Math.sqrt((dx * dx) + (dy * dy)) || 1;
  const ux = dx / length;
  const uy = dy / length;
  const wing = side => `${head.x - (ux * 5.5) - (uy * 4 * side)} ${head.y - (uy * 5.5) + (ux * 4 * side)}`;
  arrow.style.left = '0';
  arrow.style.top = '0';
  arrow.setAttribute('width', String(box.width));
  arrow.setAttribute('height', String(Math.max(tail.y, head.y) + 10));
  arrow.innerHTML = `<path d="M${tail.x} ${tail.y} C${start.x} ${start.y} ${bend.x} ${bend.y} ${head.x} ${head.y}" fill="none" stroke="#9A9A9A" stroke-width="1" stroke-linecap="round"></path>`
    + `<path d="M${wing(1)} L${head.x} ${head.y} L${wing(-1)}" fill="none" stroke="#9A9A9A" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"></path>`;
}

function aimNote(className) {
  const note = el('p', `${className} is-keyed`);
  const rest = ' are districts the other party won in 2024 that a state’s new map was drawn to flip.';
  note.appendChild(el('i', 'aim-note-key'));
  const text = el('span');
  const lead = el('strong');
  lead.textContent = 'Targeted seats';
  text.appendChild(lead);
  text.appendChild(document.createTextNode(rest));
  note.appendChild(text);
  return note;
}

function aimColumn(party, model, narrow) {
  const aim = model.aims[party];
  const column = el('div', `aim-col aim-col-${party}`);
  // Court-ordered states (Utah) get an asterisk on the map, keyed to the house card footnote.
  const courtStates = model.states.filter(state => state.seeker === party && state.drawnBy === 'court');
  const intro = el('p', 'aim-intro');
  const name = el('span', 'aim-name');
  name.style.color = PARTY[party].ink;
  name.textContent = PARTY[party].plural;
  intro.appendChild(name);
  intro.appendChild(document.createTextNode(' targeted '));
  const count = el('span', 'mono');
  count.textContent = String(aim.aim);
  intro.appendChild(count);
  intro.appendChild(document.createTextNode(' seats in '));
  const states = el('span', 'mono');
  states.textContent = String(aim.states);
  intro.appendChild(states);
  intro.appendChild(document.createTextNode(' states…'));
  column.appendChild(intro);

  const mapWrap = el('div', 'nat-wrap');
  mapWrap.appendChild(nationalMap(
    party,
    model.states.filter(state => state.seeker === party).map(state => state.code),
    courtStates.map(state => state.code),
  ));
  column.appendChild(mapWrap);
  if (party === 'R' && !narrow) {
    column.appendChild(aimNote('aim-note is-floating'));
    const arrow = svgEl('svg');
    arrow.setAttribute('class', 'aim-arrow');
    arrow.setAttribute('aria-hidden', 'true');
    column.appendChild(arrow);
  }

  // Shown from the start ("…and flipped 0 so far"), even before any results.
  {
    const flipped = el('p', 'aim-intro');
    flipped.appendChild(document.createTextNode('…and flipped '));
    const got = el('strong');
    got.style.color = PARTY[party].ink;
    got.textContent = String(aim.got);
    flipped.appendChild(got);
    // "so far" until every targeted race is decided.
    if (aim.pending) {
      flipped.appendChild(document.createTextNode(' so far'));
    }
    column.appendChild(flipped);
  }

  const clusters = el('div', 'aim-clusters');
  const before = el('div', 'aim-cluster');
  before.appendChild(miniSquares(aim.previous, seat => (PARTY[seat.prevHolder] ? PARTY[seat.prevHolder].fill : GRAY)));
  const beforeLabel = el('span', 'micro');
  // Two parts, so on narrow screens "in 2024" can drop below the party name.
  [`Won by ${PARTY[party === 'D' ? 'R' : 'D'].plural}`, 'in 2024'].forEach((text, index) => {
    // A real space for screen readers and copying; flex layout ignores it visually.
    if (index) beforeLabel.appendChild(document.createTextNode(' '));
    const part = el('span');
    part.textContent = text;
    beforeLabel.appendChild(part);
  });
  before.appendChild(beforeLabel);
  const arrow = el('span', 'aim-between');
  // Both parties read left to right: 2024 first, then the 2026 result.
  arrow.textContent = '→';
  const after = el('div', 'aim-cluster');
  after.appendChild(miniSquares(aim.outcome, (seat) => {
    const party = seat.called && partyStyle(seat, seat.winner);
    return party ? party.fill : GRAY;
  }));
  const afterLabel = el('span', 'micro');
  afterLabel.textContent = '2026 result';
  after.appendChild(afterLabel);
  [before, arrow, after].forEach(node => clusters.appendChild(node));
  column.appendChild(clusters);
  return column;
}

// Footnote for the asterisked court-ordered states on the national maps.
function courtFootnote(model) {
  const court = model.states.filter(state => state.drawnBy === 'court');
  if (!court.length) return null;
  const note = el('p', 'house-footnote');
  note.textContent = court.map(state => `*In ${state.name}, a court-ordered map created a ${state.seeker === 'D' ? 'Democratic' : 'Republican'} pickup opportunity.`).join(' ');
  return note;
}

function whyBlock(model, ui, handlers) {
  const block = el('div', 'why');
  const panel = el('div', `why-panel${ui.whyOpen ? ' is-open' : ''}`);
  const inner = el('div', 'why-panel-inner');
  panel.appendChild(inner);
  block.appendChild(panel);

  const copy = el('div', 'why-copy');
  const title = el('h3');
  title.textContent = 'A few seats could decide the House';
  const text = el('p');
  text.textContent = 'The party not in the White House typically picks up seats in midterm elections. Republicans’ historically small margin in the House and a shrinking universe of competitive districts means that even a few seats could determine control of Congress.';
  copy.appendChild(title);
  copy.appendChild(text);
  inner.appendChild(copy);

  const chart = el('div', 'why-chart');
  const head = el('div', 'why-head');
  const chartLabel = el('span');
  chartLabel.textContent = '2024 House election results';
  const majority = el('span', 'why-majority');
  majority.textContent = 'Majority';
  head.appendChild(chartLabel);
  head.appendChild(majority);
  const grid = el('div', 'why-grid');
  for (let i = 0; i < 435; i += 1) {
    const cell = el('i');
    if (i < model.baseline.D) cell.style.background = PARTY.D.fill;
    else if (i < model.baseline.D + model.baseline.R) cell.style.background = PARTY.R.fill;
    else cell.style.background = GRAY;
    grid.appendChild(cell);
  }
  const rows = ui.narrow ? 15 : 5;
  const cols = Math.ceil(435 / rows);
  const col = Math.floor(218 / rows);
  const rem = 218 % rows;
  const d = `M${col + 1} ${-4 / rows}V${rem - 1}L${col} ${rem}V${rows + 2 / rows}`;
  grid.style.setProperty('--why-rows', rows);
  grid.style.setProperty('--why-cols', cols);
  majority.style.left = `${((col + 1) / cols) * 100}%`;
  const line = svgEl('svg');
  line.setAttribute('viewBox', `0 0 ${cols} ${rows}`);
  line.setAttribute('preserveAspectRatio', 'none');
  line.innerHTML = `<path d="${d}" fill="none" stroke="#FFFFFF" stroke-width="5" stroke-linejoin="miter" vector-effect="non-scaling-stroke"></path><path d="${d}" fill="none" stroke="#262626" stroke-width="2" stroke-linejoin="miter" vector-effect="non-scaling-stroke"></path>`;
  grid.appendChild(line);
  const caption = el('div', 'why-caption');
  caption.innerHTML = '<span>Democratic seats</span><span>Republican seats</span>';
  chart.appendChild(head);
  chart.appendChild(grid);
  chart.appendChild(caption);
  inner.appendChild(chart);
  // The asterisk note for the national maps shows only when the panel is open.
  const footnote = courtFootnote(model);
  if (footnote) inner.appendChild(footnote);

  const button = el('button', 'toggle why-toggle');
  button.type = 'button';
  button.setAttribute('aria-expanded', ui.whyOpen ? 'true' : 'false');
  const label = el('span');
  label.textContent = 'Why it matters';
  const chev = chevron(ui.whyOpen);
  button.appendChild(label);
  button.appendChild(chev);
  button.addEventListener('click', () => {
    const open = !panel.classList.contains('is-open');
    panel.classList.toggle('is-open', open);
    chev.classList.toggle('is-open', open);
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    handlers.onToggleWhy(open, panel);
  });
  block.appendChild(button);
  return block;
}

function legendItem(swatch, text) {
  const item = el('span', 'legend-item');
  item.appendChild(swatch);
  item.appendChild(document.createTextNode(text));
  return item;
}

// Independents and third parties get legend entries only once one wins or leads a seat.
function otherStyles(model, test) {
  const styles = new Map();
  model.others.filter(test).forEach((seat) => {
    const style = partyStyle(seat, 'O');
    if (!styles.has(style.short)) styles.set(style.short, style);
  });
  return [...styles.values()];
}

// `labels` overrides a party's short name in this legend only (e.g. "Rep." on the House card).
function partyLegend(legend, model, labels = {}) {
  [PARTY.D, PARTY.R].concat(otherStyles(model, seat => seat.called)).forEach((style) => {
    const dot = el('i', 'legend-dot');
    dot.style.background = style.fill;
    legend.appendChild(legendItem(dot, labels[style.short] || style.short));
  });
}

function partialLegend(legend, model) {
  const pair = el('span', 'legend-pair');
  [PARTY.D, PARTY.R].concat(otherStyles(model, seat => seat.isLeading)).forEach((style) => {
    const dot = el('i', 'legend-dot');
    dot.style.background = style.tint;
    pair.appendChild(dot);
  });
  legend.appendChild(legendItem(pair, 'Leading'));
}

function runoffLegend(legend, model) {
  if (!model.runoffs.open.length) return;
  const swatch = el('i', 'legend-dot');
  swatch.style.background = RUNOFF_FILL;
  legend.appendChild(legendItem(swatch, 'Headed to runoff'));
}

// Mobile only: runoff seats have no square to tap there, so the counting rule
// that desktop puts in the seat tooltips sits under the bar instead.
// Undecided runoffs count for neither party; same-party runoffs count for that party.
function runoffNote(model) {
  const { open, decided } = model.runoffs;
  if (!open.length && !decided.length) return null;
  const note = el('p', 'stack-runoff-note');
  const names = (list) => {
    const codes = list.map(seat => districtCode(seat));
    return codes.length > 1 ? `${codes.slice(0, -1).join(', ')} and ${codes[codes.length - 1]}` : codes[0];
  };
  // Keeps "Dec. 12" on one line.
  const when = seat => runoffWords(seat).replace(/\. /g, '.\u00a0');
  const parts = [];
  if (open.length) {
    parts.push(open.length === 1
      ? `${districtCode(open[0])} is headed to a ${when(open[0])} and isn’t counted for either party yet.`
      : `${names(open)} are headed to runoffs and aren’t counted for either party yet.`);
  }
  ['D', 'R'].forEach((party) => {
    const same = decided.filter(seat => seat.winner === party);
    if (!same.length) return;
    parts.push(`Two ${PARTY[party].plural} advanced to the runoff in ${names(same)}, so ${same.length === 1 ? 'it counts' : 'they count'} as ${party === 'D' ? 'Democratic' : 'Republican'}.`);
  });
  note.textContent = parts.join(' ');
  return note;
}

function houseLegend(model, ui, handlers) {
  const legend = el('div', 'legend');
  const title = el('span', 'legend-title');
  title.textContent = 'House seats';
  legend.appendChild(title);
  if (model.mode === 'none') {
    const solid = el('i', 'legend-bar');
    const squares = el('span', 'legend-pair');
    squares.appendChild(el('i', 'legend-sq'));
    squares.appendChild(el('i', 'legend-sq'));
    legend.appendChild(legendItem(solid, 'States without new maps'));
    legend.appendChild(legendItem(squares, 'States with new maps'));
    legend.appendChild(legendItem(el('i', 'legend-target'), 'Seats targeted by new maps'));
    return legend;
  }

  partyLegend(legend, model, { [PARTY.R.short]: 'Rep.' });
  if (model.mode === 'partial') partialLegend(legend, model);
  // The mobile blocks only hold won or leading seats, so runoff, tied and
  // no-results seats aren't drawn there and get no swatch.
  if (!ui.narrow) runoffLegend(legend, model);
  // Tied and no-results seats get their own swatches, only while any are on the bar.
  if (!ui.narrow && model.ordered.some(seat => isTie(seat))) {
    const tie = el('i', 'legend-dot');
    tie.style.background = TIE_FILL;
    legend.appendChild(legendItem(tie, 'Tied'));
  }
  if (!ui.narrow && model.ordered.some(seat => !seat.shown && !seat.runoff && !isTie(seat))) {
    const none = el('i', 'legend-dot');
    none.style.background = GRAY;
    legend.appendChild(legendItem(none, 'No results'));
  }
  const target = el('i', 'legend-target');
  legend.appendChild(legendItem(target, 'Seats targeted by new maps'));

  const on = model.showFlipped;
  const toggle = el('button', 'flip-toggle');
  toggle.type = 'button';
  toggle.disabled = !model.anyFlipped;
  if (toggle.disabled) toggle.title = 'No seats have flipped yet';
  toggle.setAttribute('aria-pressed', on ? 'true' : 'false');
  const track = el('span', `switch${on ? ' is-on' : ''}`);
  track.appendChild(el('span', 'knob'));
  const words = el('span', 'flip-words');
  ['Show flipped', 'Hide flipped'].forEach((text, index) => {
    const option = el('span');
    option.textContent = text;
    if (on !== (index === 1)) option.setAttribute('aria-hidden', 'true');
    words.appendChild(option);
  });
  const sample = el('i', 'flip-sample');
  toggle.appendChild(track);
  toggle.appendChild(words);
  toggle.appendChild(sample);
  toggle.addEventListener('click', handlers.onToggleFlipped);
  legend.appendChild(toggle);
  return legend;
}

// State and district card maps are sized by area so every state looks about as
// big as California at 100px tall, within a 130 × 100 box. state-maps.json fits each
// state in 80 × 80, so a fixed scale left Tennessee tiny. Strokes are in map units.
const MAP_AREA = 100 * 87.5;
const MAP_MAX_W = 130;
const MAP_MAX_H = 100;
const MAP_HALO = 5.6;
const MAP_LINE = 0.25;

function mapScale(map) {
  return Math.min(Math.sqrt(MAP_AREA / (map.w * map.h)), MAP_MAX_W / map.w, MAP_MAX_H / map.h);
}

// Keeps titles clear of a header map of this width.
function reserveMapRoom(header, map) {
  header.style.paddingRight = `${Math.round(map.w * mapScale(map)) + 32}px`;
}

// SVG version of the bar's hatched runoff square, for map shapes.
let hatchCount = 0;
function runoffHatch(svg) {
  let pattern = svg.querySelector('pattern.runoff-hatch');
  if (!pattern) {
    hatchCount += 1;
    pattern = svgEl('pattern');
    pattern.setAttribute('class', 'runoff-hatch');
    pattern.setAttribute('id', `rd-runoff-hatch-${hatchCount}`);
    pattern.setAttribute('patternUnits', 'userSpaceOnUse');
    pattern.setAttribute('width', '2.4');
    pattern.setAttribute('height', '2.4');
    pattern.setAttribute('patternTransform', 'rotate(45)');
    pattern.innerHTML = '<rect width="2.4" height="2.4" fill="#FFFFFF"></rect><rect width="1.2" height="2.4" fill="#8A8A8A"></rect>';
    const defs = svgEl('defs');
    defs.appendChild(pattern);
    svg.insertBefore(defs, svg.firstChild);
  }
  return `url(#${pattern.getAttribute('id')})`;
}

function stateMap(state, seats) {
  const map = stateMaps[state.code];
  if (!map) return null;
  const svg = svgEl('svg');
  svg.setAttribute('class', 'state-map');
  svg.setAttribute('width', String(Math.round(map.w * mapScale(map))));
  svg.setAttribute('height', String(Math.round(map.h * mapScale(map))));
  svg.setAttribute('viewBox', `0 0 ${map.w} ${map.h}`);
  svg.setAttribute('aria-label', `${state.name} congressional districts`);
  const halo = svgEl('path');
  const all = Object.keys(map.d).map(key => map.d[key]).join('');
  halo.setAttribute('d', all);
  halo.setAttribute('fill', '#FFFFFF');
  halo.setAttribute('stroke', '#FFFFFF');
  halo.setAttribute('stroke-width', String(MAP_HALO));
  halo.setAttribute('stroke-linejoin', 'round');
  halo.setAttribute('stroke-linecap', 'round');
  svg.appendChild(halo);
  Object.keys(map.d).forEach((key) => {
    const seat = seats.find(item => item.district === Number(key));
    const path = svgEl('path');
    path.setAttribute('d', map.d[key]);
    path.setAttribute('stroke', '#FFFFFF');
    path.setAttribute('stroke-width', String(MAP_LINE));
    path.setAttribute('stroke-linejoin', 'round');
    const targeted = seat && seat.targeted;
    let fill = '#DCDCDC';
    if (targeted) fill = seat.runoff && !seat.called ? runoffHatch(svg) : seatColor(seat);
    path.setAttribute('fill', fill);
    svg.appendChild(path);
  });
  return svg;
}

// Arrow between two values, hidden from screen readers in favor of "to".
function pairArrow() {
  const arrow = svgEl('svg');
  arrow.setAttribute('class', 'pair-arrow');
  arrow.setAttribute('width', '12');
  arrow.setAttribute('height', '8');
  arrow.setAttribute('viewBox', '0 0 12 8');
  arrow.setAttribute('aria-hidden', 'true');
  const shaft = svgEl('path');
  shaft.setAttribute('d', 'M0.5 4H10.5M7.5 1L10.5 4L7.5 7');
  shaft.setAttribute('fill', 'none');
  shaft.setAttribute('stroke', 'currentColor');
  shaft.setAttribute('stroke-width', '1.2');
  shaft.setAttribute('stroke-linecap', 'round');
  shaft.setAttribute('stroke-linejoin', 'round');
  arrow.appendChild(shaft);
  const spoken = el('span', 'sr-only');
  spoken.textContent = ' to ';
  return [arrow, spoken];
}

// Each label is its own line.
function headCell(...labels) {
  const cell = el('span', 'table-head-cell');
  labels.forEach((label) => {
    const line = el('span');
    line.textContent = label;
    cell.appendChild(line);
  });
  return cell;
}

// The map comes first (cause), then the House result (effect). The map
// column shows the 2024 presidential vote inside the old and new lines, so it
// can't be mistaken for a change between the 2024 and 2026 elections.
function analysisTable(title, rows, empty) {
  const section = el('div', 'analysis');
  const heading = el('h3');
  heading.textContent = title;
  section.appendChild(heading);
  if (!rows.length) {
    const note = el('p', 'empty-note');
    note.textContent = empty;
    section.appendChild(note);
    return section;
  }
  if (title === 'Targeted districts') {
    const explain = el('p', 'explain');
    explain.textContent = 'How the new map redrew each district, measured by the 2024 presidential vote.';
    section.appendChild(explain);
  }
  const head = el('div', 'table-head');
  head.appendChild(headCell('District'));
  head.appendChild(headCell('Old → new map*'));
  const resultHead = headCell('House', '’24 → ’26');
  resultHead.classList.add('is-result');
  head.appendChild(resultHead);
  section.appendChild(head);
  rows.forEach((seat) => {
    const row = el('div', 'table-row');
    const id = el('span', 'table-district');
    id.textContent = districtCode(seat);
    id.title = districtName(seat);

    const margins = el('span', 'margin-pair');
    if (seat.margins) {
      const value = (margin) => {
        const text = marginText(margin);
        const node = el('span', 'margin-value');
        node.textContent = text.text;
        node.style.color = text.ink;
        return node;
      };
      const before = el('span', 'sr-only');
      before.textContent = 'Old map ';
      margins.appendChild(before);
      margins.appendChild(value(seat.margins.old));
      const [arrow, spoken] = pairArrow();
      margins.appendChild(spoken);
      margins.appendChild(arrow);
      margins.appendChild(value(seat.margins.new));
    } else {
      margins.textContent = '—';
    }

    const result = el('span', 'pair-squares');
    const prior = el('i');
    prior.style.background = PARTY[seat.prevHolder] ? PARTY[seat.prevHolder].fill : GRAY;
    const latest = el('i');
    // Flipped seats get the same white dot as the state card squares.
    const flipped = seat.called && seat.flipped;
    latest.style.background = flipped
      ? `radial-gradient(circle, #FFFFFF 0 1.5px, ${seatColor(seat)} 2px)`
      : seatColor(seat);
    const words = el('span', 'sr-only');
    const holder = PARTY[seat.prevHolder] ? PARTY[seat.prevHolder].short : 'unknown';
    words.textContent = `House winner: 2024 ${holder}, 2026 ${resultTone(seat).words}${flipped ? ', flipped' : ''}`;
    const [arrow] = pairArrow();
    prior.setAttribute('aria-hidden', 'true');
    latest.setAttribute('aria-hidden', 'true');
    result.appendChild(prior);
    result.appendChild(arrow);
    result.appendChild(latest);
    result.appendChild(words);

    row.appendChild(id);
    row.appendChild(margins);
    row.appendChild(result);
    section.appendChild(row);
  });
  const footnote = el('p', 'table-footnote');
  footnote.textContent = '*2024 presidential vote margin inside each district’s old and new boundaries.';
  section.appendChild(footnote);
  return section;
}

function stateCard(state, model, ui, handlers) {
  const open = !!ui.openStates[state.code];
  const card = el('article', `state-card${open ? ' is-open' : ''}`);
  const top = el('div', 'state-top');
  top.setAttribute('data-cardtop', '1');
  const header = el('div', 'state-header');
  const titles = el('div', 'state-titles');
  const name = el('h2');
  name.textContent = state.name;
  const drawn = el('span', 'drawn-badge');
  const byCourt = state.drawnBy === 'court';
  const drawnColor = !byCourt && PARTY[state.seeker] ? PARTY[state.seeker].ink : '#605D5D';
  drawn.style.color = drawnColor;
  drawn.style.borderColor = drawnColor;
  // The badge is set in all caps, so it takes the caps label (DEM, not Dem.).
  drawn.textContent = byCourt ? 'Court-ordered map' : `${PARTY[state.seeker].caps}-drawn map`;
  titles.appendChild(name);
  titles.appendChild(drawn);
  header.appendChild(titles);
  // The map sits in the header, vertically centered on the name and badge.
  const map = stateMap(state, state.seats);
  if (map) {
    header.appendChild(map);
    reserveMapRoom(header, stateMaps[state.code]);
  }
  top.appendChild(header);

  const net = el('div', 'net-row');
  const netLabel = el('span');
  netLabel.textContent = 'Statewide net change';
  const chip = el('span', 'net-value');
  chip.style.color = PARTY[state.tone] ? PARTY[state.tone].ink : '#605D5D';
  chip.textContent = state.chip;
  net.appendChild(netLabel);
  net.appendChild(chip);
  const summary = el('div', 'target-summary');
  const sentence = el('p');
  const strong = el('strong');
  strong.textContent = String(state.got);
  sentence.appendChild(strong);
  sentence.appendChild(document.createTextNode(
    // "so far" until every targeted race in the state is decided.
    ` of ${state.targeted.length} targeted seat${state.targeted.length === 1 ? '' : 's'} flipped${state.targeted.some(seat => !seat.called) ? ' so far' : ''}`,
  ));
  const compare = el('div', 'compare');
  const from = el('span', 'mono micro');
  from.textContent = '2024';
  const to = el('span', 'mono micro');
  to.textContent = '2026';
  const arrow = el('span', 'tiny-arrow');
  arrow.textContent = '→';
  compare.appendChild(from);
  state.targeted.forEach((seat) => {
    const box = el('i');
    box.title = districtName(seat);
    box.style.background = PARTY[seat.prevHolder] ? PARTY[seat.prevHolder].fill : GRAY;
    compare.appendChild(box);
  });
  compare.appendChild(arrow);
  compare.appendChild(to);
  const rank = (seat) => {
    if (seat.shown === 'D') return seat.called ? 0 : 1;
    if (seat.shown === 'R') return seat.called ? 4 : 3;
    return 2;
  };
  state.targeted.slice().sort((a, b) => rank(a) - rank(b)).forEach((seat) => {
    const box = el('i');
    box.title = districtName(seat);
    box.style.background = seatColor(seat);
    compare.appendChild(box);
  });
  summary.appendChild(sentence);
  summary.appendChild(compare);
  top.appendChild(summary);
  const netBlock = el('div', 'net-block');
  netBlock.appendChild(net);
  if (state.netNote) {
    const netNote = el('p', 'net-note');
    netNote.textContent = state.netNote;
    netBlock.appendChild(netNote);
  }

  const extra = el('div', 'state-extra');
  ['after', 'before'].forEach((field) => {
    const block = el('div', `unit-row${field === 'before' ? ' is-context' : ''}`);
    const head = el('div', 'unit-head');
    const label = el('span', 'mono');
    label.textContent = field === 'after' ? '2026 House' : '2024 House';
    const counts = field === 'after' ? state.afterCounts : state.beforeCounts;
    const tally = el('span', 'unit-tally');
    if (field === 'after' && !state.seats.some(seat => seat.called || seat.isLeading)) {
      tally.textContent = '\u2014';
    } else {
      // Called races, each followed by that party's leads in gray ("D 24 +12"),
      // matching the solid and tinted squares.
      const leads = counts.leading || {};
      const spoken = [];
      [['D', 'D', 'Democrats'], ['R', 'R', 'Republicans'], ['O', 'Other', 'Other']]
        .filter(([party]) => party !== 'O' || counts.O || leads.O)
        .forEach(([party, label, name], index) => {
          if (index) tally.appendChild(document.createTextNode(' · '));
          tally.appendChild(document.createTextNode(`${label} ${counts[party] || 0}`));
          spoken.push(`${name} ${counts[party] || 0} seats`);
          if (leads[party]) {
            const leading = el('span', 'unit-tally-leading');
            leading.textContent = ` +${leads[party]}`;
            tally.appendChild(leading);
            spoken[spoken.length - 1] += `, ${leads[party]} leading`;
          }
        });
      tally.setAttribute('aria-label', spoken.join('; '));
    }
    head.appendChild(label);
    head.appendChild(tally);
    const squares = el('div', 'square-grid');
    stateSquares(state.seats, field).forEach((item) => {
      const box = el('i');
      box.setAttribute('aria-label', item.title);
      if (field === 'after') box.setAttribute('data-seat', item.id);
      box.style.background = item.fill;
      box.style.boxShadow = item.shadow;
      squares.appendChild(box);
    });
    const note = el('p', 'unit-note');
    note.textContent = field === 'after' ? state.resultNote : state.aimNote;
    block.appendChild(head);
    block.appendChild(note);
    block.appendChild(squares);
    extra.appendChild(block);
  });
  top.appendChild(extra);
  top.appendChild(netBlock);
  card.appendChild(top);

  const detail = el('div', `detail${open ? ' is-open' : ''}`);
  const inner = el('div', 'detail-inner');
  inner.appendChild(analysisTable('Targeted districts', state.targeted));
  inner.appendChild(analysisTable('Other seat flips', state.otherFlips, 'No seats outside the targeted districts changed party.'));
  detail.appendChild(inner);
  card.appendChild(detail);

  const button = el('button', 'toggle state-toggle');
  button.type = 'button';
  button.setAttribute('aria-expanded', open ? 'true' : 'false');
  const words = el('span');
  if (ui.narrow) words.textContent = open ? 'Hide details' : 'Show details';
  else words.textContent = open ? 'Hide district analysis' : 'Show district analysis';
  button.appendChild(words);
  button.appendChild(chevron(open));
  button.addEventListener('click', () => handlers.onToggleState(state.code));
  card.appendChild(button);
  return card;
}

const openCandidates = new Set();

// Before any votes are counted, the Democrat and Republican lead the list.
function rankCandidates(rows) {
  if (rows.some(candidate => candidate.votes > 0)) return rows;
  const lead = ['D', 'R']
    .map(party => rows.find(candidate => candidate.party === party))
    .filter(Boolean);
  return lead.concat(rows.filter(candidate => !lead.includes(candidate)));
}

const RESULT_COLORS = {
  D: { win: '#008AEF', lead: '#84BBFA', leadInk: '#262626' },
  R: { win: '#CC0000', lead: '#ED9A9A', leadInk: '#262626' },
  G: { win: '#6BBD11', lead: '#C4E5A0', leadInk: '#262626' },
  I: { win: '#7A20A1', lead: '#D3A7E6', leadInk: '#262626' },
  L: { win: '#FF8E02', lead: '#FFD29A', leadInk: '#262626' },
};
const PARTY_NAMES = {
  D: 'Democrat', R: 'Republican', G: 'Green', I: 'Independent', L: 'Libertarian',
};
// Same gray as the no-results squares on the bar and state cards.
const NO_RESULTS = GRAY;
// White text on the Democratic fill is 3.5:1, so chips use the darker text blue.
const CHIP_BLUE = PARTY.D.ink;
const RUNOFF = '#605D5D';

// Independents and third parties ('O') use their own party's colors.
function resultColors(seat, party) {
  if (party === 'O') return RESULT_COLORS[seat.otherColor] || RESULT_COLORS.I;
  return RESULT_COLORS[party];
}

function resultTone(seat) {
  const hasVotes = seat.candidates.some(candidate => candidate.votes > 0);
  if (seat.runoff) {
    const colors = seat.called && resultColors(seat, seat.winner);
    const words = runoffWords(seat);
    return {
      color: colors ? colors.win : RUNOFF,
      chip: seat.winner === 'D' ? CHIP_BLUE : null,
      ink: '#FFFFFF',
      words: words[0].toUpperCase() + words.slice(1),
    };
  }
  if (seat.called && resultColors(seat, seat.winner)) {
    return {
      color: resultColors(seat, seat.winner).win,
      chip: seat.winner === 'D' ? CHIP_BLUE : null,
      ink: '#FFFFFF',
      words: `${partyStyle(seat, seat.winner).short} win`,
    };
  }
  if (!seat.called && resultColors(seat, seat.leader)) {
    const colors = resultColors(seat, seat.leader);
    return { color: colors.lead, ink: colors.leadInk, words: `${partyStyle(seat, seat.leader).short} leading` };
  }
  if (hasVotes) return { color: TIE_FILL, ink: '#FFFFFF', words: 'Tie' };
  return { color: NO_RESULTS, ink: '#262626', words: 'No results' };
}

// `short` trims labels for the narrow tooltip.
function candidateRows(seat, short = false) {
  const rows = seat.candidates.filter(candidate => candidate.party || candidate.name);
  const source = rows.length ? rankCandidates(rows) : [
    {
      party: 'D', color: 'D', name: 'Democrat', pct: null, winner: false,
    },
    {
      party: 'R', color: 'R', name: 'Republican', pct: null, winner: false,
    },
  ];
  return source.map((candidate) => {
    const won = seat.called && candidate.winner;
    const row = el('div', `cand${won ? ' is-winner' : ''}`);
    // Parties without their own color (e.g. No Party Affiliation) use the independent purple, as on the maps.
    const colors = RESULT_COLORS[candidate.color || candidate.party] || (candidate.partyName ? RESULT_COLORS.I : null);
    const color = colors ? colors.win : NO_RESULTS;
    const share = candidate.votes > 0 ? parseFloat(candidate.pct) || 0 : 0;
    const bar = el('span', 'cand-bar');
    bar.style.width = `${Math.min(100, share)}%`;
    bar.style.borderLeftColor = color;
    bar.style.background = `${color}55`;
    const name = el('span', 'cand-name');
    const label = el('span', 'cand-label');
    const text = el('span', 'cand-text');
    text.textContent = candidate.name || 'Candidate';
    label.appendChild(text);
    if (won) {
      const check = svgEl('svg');
      check.setAttribute('class', 'cand-check');
      check.setAttribute('width', '14');
      check.setAttribute('height', '14');
      check.setAttribute('viewBox', '0 0 14 14');
      check.setAttribute('role', 'img');
      check.setAttribute('aria-label', 'Winner');
      check.innerHTML = '<circle cx="7" cy="7" r="7" fill="#0C0C0C"></circle><path d="M4 7.2 6.1 9.2 10 5" fill="none" stroke="#FFFFFF" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"></path>';
      label.appendChild(check);
    }
    name.appendChild(label);
    const partyName = PARTY_NAMES[candidate.color || candidate.party] || candidate.partyName;
    let advances = '';
    if (seat.runoff && candidate.runoff) advances = short ? 'Runoff' : 'Advances to runoff';
    if ((partyName && partyName !== candidate.name) || advances) {
      const party = el('span', 'cand-party');
      party.textContent = [partyName !== candidate.name && partyName, advances].filter(Boolean).join(' · ');
      name.appendChild(party);
    }
    const pct = el('span', 'cand-pct');
    pct.appendChild(document.createTextNode(candidate.votes > 0 && candidate.pct ? `${String(candidate.pct).replace(/\.0+$/, '')}%` : '—'));
    row.appendChild(bar);
    row.appendChild(name);
    row.appendChild(pct);
    return row;
  });
}

const SMALL_DISTRICTS = new Set(['FL-14']);

function badgeWords(seat, tone) {
  if (seat.runoff) return tone.words.toUpperCase();
  if (seat.called && seat.winner) return 'PROJECTED WINNER';
  if (!seat.called && seat.leader) return 'LEADING';
  return tone.words.toUpperCase();
}

// What a screen reader hears for a chip; the visible chip relies on color for the party.
function chipSpoken(seat, tone) {
  const name = party => (partyStyle(seat, party) || {}).name;
  if (seat.runoff) {
    const adjective = { D: 'Democratic', R: 'Republican' }[seat.winner] || name(seat.winner);
    return seat.called && name(seat.winner)
      ? `${adjective} seat, ${runoffWords(seat)}`
      : `Headed to ${runoffWords(seat)}`;
  }
  if (seat.called && name(seat.winner)) return `${name(seat.winner)} projected winner`;
  if (!seat.called && name(seat.leader)) return `${name(seat.leader)} leading`;
  return tone.words;
}

function resultChip(seat, tone) {
  const chip = el('span', 'chip');
  chip.style.background = tone.chip || tone.color;
  chip.style.color = tone.ink;
  const shown = el('span');
  shown.setAttribute('aria-hidden', 'true');
  shown.textContent = badgeWords(seat, tone);
  const spoken = el('span', 'sr-only');
  spoken.textContent = chipSpoken(seat, tone);
  chip.appendChild(shown);
  chip.appendChild(spoken);
  if (seat.called && seat.winner && !seat.runoff) {
    const check = svgEl('svg');
    check.setAttribute('class', 'chip-check');
    check.setAttribute('width', '12');
    check.setAttribute('height', '12');
    check.setAttribute('viewBox', '0 0 14 14');
    check.setAttribute('aria-hidden', 'true');
    check.innerHTML = `<circle cx="7" cy="7" r="7" fill="#FFFFFF"></circle><path d="M4 7.2 6.1 9.2 10 5" fill="none" stroke="${tone.chip || tone.color}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path>`;
    chip.appendChild(check);
  }
  return chip;
}

function targetOutcome(seat) {
  const seeker = PARTY[seat.seeker].plural;
  const flipped = seat.called && seat.winner === seat.seeker;
  const held = seat.called && !flipped && PARTY[seat.winner];
  if (seat.runoff && !seat.called) {
    return [`${seeker} targeted this seat. It's headed to a ${runoffWords(seat)} and isn't counted for either party yet.`, '', ''];
  }
  if (seat.runoff && PARTY[seat.winner]) {
    const both = `Two ${PARTY[seat.winner].plural} advanced to the ${runoffWords(seat)}`;
    if (flipped) return [`${seeker} targeted this seat and will `, 'flip', ` it. ${both}.`];
    return [`${seeker} targeted this seat. ${both}, so ${PARTY[seat.winner].plural} `, 'will hold', ' it.'];
  }
  if (seat.called && seat.winner === 'O') {
    const who = { Independent: 'An independent', Green: 'A Green Party', Libertarian: 'A Libertarian' }[partyStyle(seat, 'O').name] || 'A third-party';
    return [`${seeker} targeted this seat. ${who} candidate `, 'won', ' it.'];
  }
  if (seat.drawnBy === 'court') {
    if (flipped) return [`${seeker} `, 'flipped', ' this seat under a court-ordered map.'];
    if (held) return [`${PARTY[seat.winner].plural} `, 'held', ' this seat under a court-ordered map.'];
    return [`A court-ordered map made this a ${seat.seeker === 'D' ? 'Democratic' : 'Republican'} pickup opportunity.`, '', ''];
  }
  if (flipped) return [`${seeker} targeted this seat and `, 'flipped', ' it.'];
  if (held) return [`${seeker} targeted this seat. ${PARTY[seat.winner].plural} `, 'held', ' it.'];
  return [`${seeker} targeted this seat with the new map.`, '', ''];
}

function targetOutcomeLine(seat) {
  const [before, verb, after] = targetOutcome(seat);
  const outcome = el('p', 'seat-tip-outcome');
  outcome.appendChild(document.createTextNode(before));
  if (verb) {
    const strong = document.createElement('strong');
    strong.textContent = verb;
    outcome.appendChild(strong);
  }
  if (after) outcome.appendChild(document.createTextNode(after));
  return outcome;
}

export function seatTip(seat) {
  const tip = el('div', 'seat-tip');
  const head = el('div', 'seat-tip-head');
  const badges = el('div', 'seat-tip-badges');
  badges.appendChild(resultChip(seat, resultTone(seat)));
  if (seat.targeted) {
    const targeted = el('span', 'chip seat-tip-target');
    targeted.textContent = 'TARGETED';
    badges.appendChild(targeted);
  }
  head.appendChild(badges);
  const title = el('h3');
  title.textContent = districtName(seat);
  head.appendChild(title);
  if (seat.area) {
    const place = el('span');
    place.textContent = seat.area;
    head.appendChild(place);
  }
  // Undecided runoffs count for neither party; same-party runoffs count for that party.
  // Targeted seats say this in their callout instead.
  if (seat.runoff && !seat.targeted) {
    const note = el('p', 'seat-tip-note');
    // Keeps "Dec. 12" on one line.
    const when = runoffWords(seat).replace(/\. /g, '.\u00a0');
    note.textContent = seat.called && PARTY[seat.winner]
      ? `Two ${PARTY[seat.winner].plural} advanced to the ${when}, so this seat counts as ${seat.winner === 'D' ? 'Democratic' : 'Republican'}.`
      : `Headed to a ${when} and not counted for either party yet.`;
    head.appendChild(note);
  }
  if (seat.targeted) {
    const callout = el('div', 'seat-tip-target-callout');
    callout.appendChild(targetOutcomeLine(seat));
    head.appendChild(callout);
  }
  tip.appendChild(head);

  const table = el('div', 'cand-table');
  candidateRows(seat, true).slice(0, 2).forEach(row => table.appendChild(row));
  tip.appendChild(table);

  const updated = updatedLine(seat);
  const estimate = estimateBlock(seat);
  if (updated || estimate) {
    const footer = el('div', 'cand-status');
    footer.appendChild(updated || el('span'));
    if (estimate) footer.appendChild(estimate);
    tip.appendChild(footer);
  }
  return tip;
}

function districtCard(seat) {
  const pending = !seat.called;
  const success = seat.called && seat.winner === seat.seeker;
  const tone = resultTone(seat);
  const headerColor = tone.color;
  const card = el('article', 'district-card');
  const map = stateMaps[seat.state];
  const headerMap = el('div', 'district-map');
  if (map && map.d[String(seat.district)]) {
    const svg = svgEl('svg');
    svg.setAttribute('class', 'state-map');
    const scale = mapScale(map);
    svg.setAttribute('width', String(Math.round(map.w * scale)));
    svg.setAttribute('height', String(Math.round(map.h * scale)));
    svg.setAttribute('viewBox', `0 0 ${map.w} ${map.h}`);
    const all = Object.keys(map.d).map(key => map.d[key]).join('');
    const halo = svgEl('path');
    halo.setAttribute('d', all);
    halo.setAttribute('fill', '#FFFFFF');
    halo.setAttribute('stroke', '#FFFFFF');
    halo.setAttribute('stroke-width', String(MAP_HALO));
    halo.setAttribute('stroke-linejoin', 'round');
    halo.setAttribute('stroke-linecap', 'round');
    const base = svgEl('path');
    base.setAttribute('d', all);
    base.setAttribute('fill', '#DCDCDC');
    base.setAttribute('stroke', '#FFFFFF');
    base.setAttribute('stroke-width', String(MAP_LINE));
    const focus = svgEl('path');
    focus.setAttribute('d', map.d[String(seat.district)]);
    const mapFill = seat.runoff && !seat.called ? runoffHatch(svg) : headerColor;
    focus.setAttribute('fill', mapFill);
    focus.setAttribute('stroke', '#FFFFFF');
    focus.setAttribute('stroke-width', String(MAP_LINE));
    svg.appendChild(halo);
    svg.appendChild(base);
    svg.appendChild(focus);
    const box = bounds(map.d[String(seat.district)]);
    let note = null;
    const sides = [box[2] - box[0], box[3] - box[1]];
    if (Math.max(...sides) < 5 || Math.min(...sides) < 2 || SMALL_DISTRICTS.has(seat.id)) {
      const cx = (box[0] + box[2]) / 2;
      const cy = (box[1] + box[3]) / 2;
      const labelX = Math.min(Math.max(cx, 24), map.w - 24);
      const dot = svgEl('circle');
      dot.setAttribute('cx', cx.toFixed(1));
      dot.setAttribute('cy', cy.toFixed(1));
      dot.setAttribute('r', '3.2');
      dot.setAttribute('fill', mapFill);
      dot.setAttribute('stroke', '#FFFFFF');
      dot.setAttribute('stroke-width', '1.2');
      svg.appendChild(dot);
      note = el('span', 'small-district-note');
      note.innerHTML = 'District<br>location';
      note.style.right = `${((map.w - labelX) * scale).toFixed(1)}px`;
      note.style.top = `${((cy * scale) - 14.5).toFixed(1)}px`;
    }
    headerMap.style.width = `${Math.round(map.w * scale)}px`;
    headerMap.style.height = `${Math.round(map.h * scale)}px`;
    headerMap.appendChild(svg);
    if (note) headerMap.appendChild(note);
  }

  const header = el('div', 'district-header');
  // Headers and target boxes line up across each row of cards on desktop.
  header.setAttribute('data-cardtop', '1');
  header.appendChild(resultChip(seat, tone));
  // Two lines at most: "North Carolina’s" and "1st District" each stay whole, so the
  // name breaks only between them, even if the first part runs over the map.
  const title = el('h3');
  const name = districtName(seat);
  const split = name.indexOf('’s ') + 2;
  [name.slice(0, split), name.slice(split + 1)].forEach((part, index) => {
    if (index) title.appendChild(document.createTextNode(' '));
    const piece = el('span', 'nowrap');
    piece.textContent = part;
    title.appendChild(piece);
  });
  const place = el('span');
  place.textContent = seat.area || '';
  header.appendChild(title);
  header.appendChild(place);
  if (headerMap.firstChild) {
    header.appendChild(headerMap);
    header.style.minHeight = `${Math.round(map.h * mapScale(map)) + 24}px`;
    reserveMapRoom(header, map);
  }
  card.appendChild(header);

  const body = el('div', 'district-body');
  const target = el('div', 'target-box');
  target.setAttribute('data-cardmid', '1');
  const badge = el('span', 'badge');
  if (success) {
    const winColor = RESULT_COLORS[seat.winner] ? RESULT_COLORS[seat.winner].win : '#262626';
    const prevColor = RESULT_COLORS[seat.prevHolder] ? RESULT_COLORS[seat.prevHolder].win : GRAY;
    badge.className = 'badge is-pair';
    badge.innerHTML = `<i style="background:${prevColor}"></i><svg width="10" height="8" viewBox="0 0 12 8" aria-hidden="true"><path d="M0.5 4H10.5M7.5 1L10.5 4L7.5 7" fill="none" stroke="#262626" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"></path></svg><i style="background:${winColor}"></i>`;
  } else {
    if (!pending) badge.className = 'badge is-miss';
    else badge.style.background = '#BDBDBD';
    const mark = pending ? 'M3 6h6' : 'M3.5 3.5l5 5M8.5 3.5l-5 5';
    const ink = pending ? '#FFFFFF' : '#262626';
    badge.innerHTML = `<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="${mark}" fill="none" stroke="${ink}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg>`;
  }
  const status = el('div', 'target-status');
  const targetLabel = el('span');
  targetLabel.textContent = 'Target';
  const statusText = el('span', 'mono status-label');
  let statusWords = success ? 'FLIPPED' : 'NOT FLIPPED';
  // A same-party runoff decides the party before the Dec. 12 vote.
  if (seat.runoff && seat.called) statusWords = success ? 'WILL FLIP' : 'WON’T FLIP';
  if (pending) statusWords = seat.runoff ? runoffWords(seat).toUpperCase() : 'NOT YET CALLED';
  statusText.textContent = statusWords;
  status.appendChild(targetLabel);
  status.appendChild(statusText);
  const sentence = el('p');
  const holders = PARTY[seat.prevHolder] ? PARTY[seat.prevHolder].plural : 'Another party';
  sentence.textContent = seat.drawnBy === 'court'
    ? `The court-ordered map made this seat, which ${holders} won in 2024, a ${seat.seeker === 'D' ? 'Democratic' : 'Republican'} pickup opportunity.`
    : `${PARTY[seat.seeker].plural} targeted this seat, which ${holders} won in 2024, with the new map.`;
  target.appendChild(badge);
  target.appendChild(status);
  target.appendChild(sentence);
  body.appendChild(target);

  const table = el('div', 'cand-table');
  const rows = candidateRows(seat);
  rows.slice(0, 2).forEach(row => table.appendChild(row));
  const extra = rows.slice(2);
  if (extra.length) {
    let open = openCandidates.has(seat.id);
    const more = el('div', 'cand-extra');
    extra.forEach(row => more.appendChild(row));
    more.hidden = !open;
    const button = el('button', 'cand-more');
    button.type = 'button';
    const words = el('span');
    const icon = chevron(open);
    const label = () => {
      words.textContent = open
        ? 'Hide other candidates'
        : `Show ${extra.length} more candidate${extra.length === 1 ? '' : 's'}`;
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      icon.classList.toggle('is-open', open);
    };
    label();
    button.appendChild(words);
    button.appendChild(icon);
    button.addEventListener('click', () => {
      open = !open;
      if (open) openCandidates.add(seat.id);
      else openCandidates.delete(seat.id);
      more.hidden = !open;
      if (window.pymChild) window.pymChild.sendHeight();
      label();
    });
    table.appendChild(more);
    table.appendChild(button);
  }
  const updated = updatedLine(seat);
  const estimate = estimateBlock(seat);
  if (updated || estimate) {
    const footer = el('div', 'cand-status');
    footer.appendChild(updated || el('span'));
    if (estimate) footer.appendChild(estimate);
    table.appendChild(footer);
  }
  body.appendChild(table);
  card.appendChild(body);
  return card;
}

function verticalBar(model) {
  const bar = model.vertical;
  const width = bar.columns * bar.cell;
  const height = bar.rows * bar.cell;
  const mobile = el('div', 'bar-mobile');
  const frame = el('div', 'vbar');
  frame.style.width = `${width}px`;
  frame.style.marginLeft = `${bar.labelGutter}px`;

  const label = el('div', 'vbar-label');
  label.style.top = `${bar.lineY}px`;
  label.textContent = '218 to win';
  frame.appendChild(label);
  frame.appendChild(seatGrid(bar.cells, bar.rows, bar.columns, bar.cell));

  const line = svgEl('svg');
  line.setAttribute('class', 'vbar-line');
  line.setAttribute('width', String(width));
  line.setAttribute('height', String(height));
  line.innerHTML = `<path d="${bar.line}" fill="none" stroke="#FFFFFF" stroke-width="5" stroke-linejoin="miter"></path><path d="${bar.line}" fill="none" stroke="#262626" stroke-width="2" stroke-linejoin="miter"></path>`;
  frame.appendChild(line);

  const brackets = el('div', 'vbar-brackets');
  bar.brackets.forEach((item) => {
    const tick = el('div', 'vbar-bracket');
    tick.style.top = `${item.top}px`;
    tick.style.height = `${item.height}px`;
    if (item.showLabel) {
      const text = el('span', 'bracket-label');
      text.textContent = item.label;
      tick.appendChild(text);
    }
    brackets.appendChild(tick);
  });
  frame.appendChild(brackets);
  mobile.appendChild(frame);
  mobile.appendChild(partyTotal('R', model, 'left'));
  return mobile;
}

const AP_MONTHS = ['Jan.', 'Feb.', 'March', 'April', 'May', 'June', 'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'];

function easternParts(date) {
  const parts = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date).forEach((part) => {
    parts[part.type] = part.value;
  });
  return {
    date: `${AP_MONTHS[Number(parts.month) - 1]} ${parts.day}`,
    time: `${parts.minute === '00' ? parts.hour : `${parts.hour}:${parts.minute}`} ${parts.dayPeriod.toLowerCase() === 'am' ? 'a.m.' : 'p.m.'} ET`,
  };
}

function updatedLine(seat) {
  const stamp = seat.updated ? new Date(seat.updated) : null;
  if (!stamp || Number.isNaN(stamp.getTime())) return null;
  const parts = easternParts(stamp);
  const updated = el('p', 'cand-updated');
  const nowrap = text => text.replace(/ /g, '\u00A0');
  updated.textContent = `${nowrap(`Updated ${parts.time},`)} ${nowrap(parts.date)}`;
  return updated;
}

// Votes can be in while the feed's estimate still rounds to 0; that reads as <1%.
function estimateBlock(seat) {
  let pct = seat.pctReporting == null ? null : Number(seat.pctReporting);
  if (pct == null || Number.isNaN(pct)) return null;
  const hasVotes = seat.hasVotes != null
    ? seat.hasVotes
    : (seat.candidates || []).some(candidate => candidate.votes > 0);
  if (pct === 0 && hasVotes) pct = 0.5;
  let shown = `${Math.round(pct)}%`;
  if (pct > 0 && pct < 1) shown = '<1%';
  else if (pct > 99 && pct < 100) shown = '>99%';
  const est = el('div', 'cand-est');
  const label = el('span');
  label.innerHTML = 'Est <strong></strong> in';
  label.querySelector('strong').textContent = shown;
  const meter = el('span', 'cand-meter');
  const fill = el('i');
  fill.style.width = `${Math.max(0, Math.min(100, pct))}%`;
  meter.appendChild(fill);
  est.appendChild(label);
  est.appendChild(meter);
  return est;
}

function houseHeadline() {
  const line = el('h2', 'house-headline');
  line.textContent = 'US House election results';
  return line;
}

function houseCard(model, ui, handlers) {
  const card = el('section', 'card house-card');
  card.appendChild(el('div', 'measurer'));
  const box = el('div', 'overview');
  const title = el('h2', 'overview-title');
  title.textContent = 'What each party set out to flip through redistricting, and what\u00a0happened';
  const columns = el('div', 'aim-cols');
  columns.appendChild(aimColumn('D', model, ui.narrow));
  if (ui.narrow) {
    const noteRow = el('div', 'aim-note-row');
    noteRow.appendChild(aimNote('aim-note below'));
    columns.appendChild(noteRow);
  }
  columns.appendChild(aimColumn('R', model, ui.narrow));
  box.appendChild(title);
  box.appendChild(columns);
  box.appendChild(whyBlock(model, ui, handlers));
  card.appendChild(box);
  card.appendChild(houseHeadline());
  // Before results the mobile bars are empty, so there's nothing for a legend to key.
  if (!(model.mode === 'none' && ui.narrow)) card.appendChild(houseLegend(model, ui, handlers));

  const head = el('div', 'bar-head');
  head.appendChild(partyTotal('D', model, 'left', ui.narrow));
  const otherHead = otherTotal(model, 'desktop-only');
  if (otherHead) head.appendChild(otherHead);
  head.appendChild(partyTotal('R', model, 'right desktop-only'));
  card.appendChild(head);

  const desktop = el('div', 'bar-desktop');
  const frame = el('div', 'bar-frame');
  frame.style.width = `${model.desktop.width}px`;
  const label = el('div', 'win-label');
  label.style.left = `${Math.ceil(218 / DESKTOP_ROWS) * model.desktop.cell}px`;
  label.textContent = '218 to win';
  frame.appendChild(label);
  frame.appendChild(seatGrid(model.desktop.cells, DESKTOP_ROWS, DESKTOP_COLS, model.desktop.cell));
  const line = svgEl('svg');
  line.setAttribute('class', 'win-line');
  line.setAttribute('width', String(model.desktop.width));
  line.setAttribute('height', String(model.desktop.height));
  line.innerHTML = `<path d="${model.desktop.line}" fill="none" stroke="#FFFFFF" stroke-width="4" stroke-linejoin="miter"></path><path d="${model.desktop.line}" fill="none" stroke="#262626" stroke-width="1.5" stroke-linejoin="miter"></path>`;
  frame.appendChild(line);
  frame.appendChild(bracketRow(model.desktop.brackets));
  desktop.appendChild(frame);
  card.appendChild(desktop);

  if (ui.mobileBar === 'vertical') {
    card.appendChild(verticalBar(model));
    return card;
  }

  const mobile = el('div', 'bar-mobile');
  const stack = el('div', 'stack');
  stack.style.width = `${model.mobile.width}px`;
  const mobileLabel = el('div', 'win-label stack-label');
  mobileLabel.style.left = `${model.mobile.lineX}px`;
  mobileLabel.innerHTML = '218<br>to win';
  stack.appendChild(mobileLabel);
  const marker = el('div', 'stack-line');
  marker.style.left = `${model.mobile.lineX}px`;
  stack.appendChild(marker);
  const empty = model.mode === 'none';
  const stackBlock = (cells, brackets, track, above) => {
    const piece = el('div', `stack-block${empty ? ' is-empty' : ''}`);
    const row = bracketRow(brackets);
    if (above) {
      row.classList.add('is-above');
      piece.appendChild(row);
    }
    const grid = seatGrid(cells, 8, model.mobile.columns, model.mobile.cell);
    track.forEach((rect) => {
      const part = el('div', 'stack-track');
      part.style.left = `${rect.left}px`;
      part.style.top = `${rect.top}px`;
      part.style.width = `${rect.width}px`;
      part.style.height = `${rect.height}px`;
      grid.appendChild(part);
    });
    piece.appendChild(grid);
    if (!above) piece.appendChild(row);
    return piece;
  };
  const democratBlock = stackBlock(
    model.mobile.democrats, model.mobile.democratBrackets, model.mobile.democratTrack, true,
  );
  stack.appendChild(democratBlock);
  const otherLine = otherTotal(model, 'is-stack');
  if (otherLine) {
    const strip = el('div', 'stack-block stack-others');
    strip.appendChild(seatGrid(model.mobile.others, model.mobile.otherRows, model.mobile.otherColumns, model.mobile.cell));
    strip.appendChild(otherLine);
    stack.appendChild(strip);
  }
  stack.appendChild(stackBlock(
    model.mobile.republicans, model.mobile.republicanBrackets, model.mobile.republicanTrack,
  ));
  mobile.appendChild(stack);
  mobile.appendChild(partyTotal('R', model, 'left'));
  const runoffs = runoffNote(model);
  if (runoffs) mobile.appendChild(runoffs);
  card.appendChild(mobile);
  return card;
}

// Preview-only control (?results=…) for cycling Louisiana through runoff and other edge cases.
function testPanel(ui, handlers) {
  const panel = el('div', 'test-panel');
  const head = el('div', 'test-panel-head');
  const title = el('strong');
  title.textContent = 'Test: Louisiana';
  head.appendChild(title);
  const shuffle = el('button', 'test-button');
  shuffle.type = 'button';
  shuffle.textContent = ui.laSeed ? 'Randomize again' : 'Randomize Louisiana';
  shuffle.addEventListener('click', handlers.onShuffleLouisiana);
  head.appendChild(shuffle);
  if (ui.laSeed) {
    const reset = el('button', 'test-button is-quiet');
    reset.type = 'button';
    reset.textContent = 'Reset';
    reset.addEventListener('click', handlers.onResetLouisiana);
    head.appendChild(reset);
  }
  panel.appendChild(head);
  if (ui.laSeed && ui.laPicks.length) {
    const list = el('ul');
    ui.laPicks.forEach((pick) => {
      const item = el('li');
      const code = el('span', 'mono');
      code.textContent = districtCode({ state: 'LA', district: Number(pick.id.split('-')[1]) });
      item.appendChild(code);
      item.appendChild(document.createTextNode(` ${pick.label}`));
      list.appendChild(item);
    });
    panel.appendChild(list);
  }
  return panel;
}

export function draw(root, model, ui, handlers) {
  root.innerHTML = '';
  root.classList.toggle('is-narrow', !!ui.narrow);
  if (ui.preview) {
    const note = el('p', 'preview-note');
    note.textContent = 'Previewing sample results because the 2026 feed did not load. These are not live returns.';
    root.appendChild(note);
  }
  if (!model) {
    if (ui.loading) {
      const loading = el('p', 'preview-note');
      loading.textContent = 'Loading results…';
      root.appendChild(loading);
      return;
    }
    const error = el('div', 'feed-error');
    const text = el('p');
    text.textContent = 'Results temporarily unavailable';
    const retry = el('p', 'retry');
    retry.textContent = 'Trying again…';
    error.appendChild(text);
    error.appendChild(retry);
    root.appendChild(error);
    return;
  }

  if (ui.stale) {
    const stale = el('p', 'preview-note stale-note');
    stale.setAttribute('role', 'status');
    let text = 'Results may be delayed.';
    if (ui.lastLoaded) {
      const parts = easternParts(ui.lastLoaded);
      text += ` Last updated ${parts.time}, ${parts.date}.`;
    }
    stale.textContent = text;
    root.appendChild(stale);
  }
  if (ui.testing) root.appendChild(testPanel(ui, handlers));
  // Each embed draws one section; the draft page draws all three.
  const show = part => ui.section === 'all' || ui.section === part;
  if (show('house')) {
    const house = houseCard(model, ui, handlers);
    root.appendChild(house);
    // The overall share of the expected vote counted sits with the "Updated" line.
    const updated = ui.preview ? null : updatedLine({ updated: model.updated });
    const estimate = ui.preview ? null : estimateBlock({
      pctReporting: model.pctReporting,
      hasVotes: model.ordered.some(seat => seat.candidates.some(candidate => candidate.votes > 0)),
    });
    if (updated || estimate) {
      const footer = el('div', 'house-status');
      if (estimate) footer.appendChild(estimate);
      if (updated) {
        // Credit for the race calls. Each part stays whole, so on a narrow screen
        // the line breaks only after the bullet.
        updated.textContent = `CNN\u00A0projections\u00A0\u2022 ${updated.textContent.replace(/ /g, '\u00A0')}`;
        updated.style.whiteSpace = 'normal';
        updated.style.textAlign = 'right';
        footer.appendChild(updated);
      }
      house.appendChild(footer);
    }
  }

  if (show('states')) {
    const states = el('section', 'states');
    // Same heading style as the district section's.
    const intro = el('div', 'district-intro');
    const introHeading = el('h2');
    introHeading.textContent = 'Results in redistricted states';
    intro.appendChild(introHeading);
    states.appendChild(intro);
    const legend = el('div', 'legend state-legend');
    const title = el('h2');
    title.textContent = 'House seats';
    legend.appendChild(title);
    partyLegend(legend, model);
    if (model.mode === 'partial') partialLegend(legend, model);
    runoffLegend(legend, model);
    const targeted = el('i', 'legend-target is-outside');
    legend.appendChild(legendItem(targeted, 'Seats targeted by new maps'));
    const flipped = el('i', 'flip-sample');
    legend.appendChild(legendItem(flipped, 'Flipped'));
    states.appendChild(legend);

    const limitStates = ui.narrow && !ui.statesOpen;
    const shownStates = limitStates ? model.states.slice(0, 3) : model.states;
    const grid = el('div', `state-grid${shownStates.length < model.states.length ? ' is-peek' : ''}`);
    shownStates.forEach(state => grid.appendChild(stateCard(state, model, ui, handlers)));
    states.appendChild(grid);
    if (ui.narrow && model.states.length > 3) {
      const more = el('button', 'more');
      more.type = 'button';
      more.textContent = ui.statesOpen ? 'Show fewer states' : `Show all ${model.states.length} states`;
      more.appendChild(chevron(ui.statesOpen));
      more.addEventListener('click', handlers.onToggleStates);
      states.appendChild(more);
    }
    root.appendChild(states);
  }

  if (show('districts')) {
    const districtSection = el('section', 'districts'); const head = el('div', 'district-intro');
    const heading = el('h2');
    heading.textContent = 'Results in targeted districts';
    head.appendChild(heading);
    districtSection.appendChild(head);

    const chips = el('div', 'chips');
    const groups = [{ code: 'ALL', name: 'All', count: model.districts.length }];
    model.states.forEach((state) => {
      groups.push({
        code: state.code,
        name: state.name,
        count: model.districts.filter(seat => seat.state === state.code).length,
      });
    });
    groups.forEach((group) => {
      const button = el('button', `chip-btn${ui.districtState === group.code ? ' is-on' : ''}`);
      button.type = 'button';
      button.textContent = `${group.name} (${group.count})`;
      button.addEventListener('click', () => handlers.onFilter(group.code));
      chips.appendChild(button);
    });
    districtSection.appendChild(chips);

    const filtered = ui.districtState === 'ALL'
      ? model.districts
      : model.districts.filter(seat => seat.state === ui.districtState);
    const shown = ui.districtOpen ? filtered : filtered.slice(0, 3);
    const cards = el('div', `district-grid${shown.length < filtered.length ? ' is-peek' : ''}`);
    shown.forEach(seat => cards.appendChild(districtCard(seat)));
    districtSection.appendChild(cards);
    if (filtered.length > 3) {
      const more = el('button', 'more');
      more.type = 'button';
      more.textContent = ui.districtOpen ? 'Show fewer districts' : `Show all ${filtered.length} districts`;
      more.appendChild(chevron(ui.districtOpen));
      more.addEventListener('click', handlers.onToggleDistricts);
      districtSection.appendChild(more);
    }
    root.appendChild(districtSection);
  }
}
